import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { employerAPI } from 'utils/api';
import Notifier from 'components/ui/Notifier';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const EmployerVDRApplicationDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vdr_id } = useParams();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [application, setApplication] = useState(null);
  const [selectionData, setSelectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState({ show: false, type: 'info', message: '' });

  useEffect(() => {
    if (user && (!user.eclabUser || user.role !== 3)) {
      navigate('/login-screen');
    }
  }, [user, navigate]);

  useEffect(() => {
    const incomingToast = location.state?.toast;
    if (incomingToast) {
      setToast({ show: true, type: incomingToast.type || 'info', message: incomingToast.message || '' });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [applicationResponse, selectionResponse] = await Promise.all([
          employerAPI.getApplication(vdr_id),
          employerAPI.getSelections(vdr_id)
        ]);
        setApplication(applicationResponse);
        setSelectionData(selectionResponse);
      } catch (err) {
        console.error('Failed to load VDR application', err);
        setError(err.response?.data?.error || err.message || 'Failed to load application');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vdr_id]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSelectWorkers = () => {
    if (!application?.isSelectable) return;
    navigate('/employer-biodata-selection', {
      state: {
        vdrApplication: application,
        fromVDRFlow: true
      }
    });
  };

  const selectedWorkers = selectionData?.selected_workers || [];
  const rejectedWorkers = selectionData?.rejected_workers || [];

  const summaryItems = useMemo(() => {
    if (!application) return [];
    const slots = application.slots || {};
    const totalSlots = slots.approved ?? slots.requested ?? application.fclRequested ?? 0;
    const submittedSlots =
      slots.used ??
      Math.max(
        totalSlots -
          (slots.remaining ?? slots.localRemaining ?? totalSlots),
        0
      );
    const remainingSlots =
      slots.remaining ?? Math.max(totalSlots - submittedSlots, 0);
    const approved = application.localStats?.workers?.approved ?? 0;
    const rejected = application.localStats?.workers?.rejected ?? 0;

    return [
      {
        label: 'Remaining Quota',
        value: remainingSlots,
        icon: 'Users',
        accent: 'text-blue-600'
      },
      {
        label: 'Submitted Workers',
        value: submittedSlots,
        icon: 'Clock',
        accent: 'text-amber-600'
      },
      {
        label: 'Approved Workers',
        value: approved,
        icon: 'CheckCircle',
        accent: 'text-green-600'
      },
      {
        label: 'Rejected Workers',
        value: rejected,
        icon: 'XCircle',
        accent: 'text-red-600'
      }
    ];
  }, [application, selectionData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderBar
          userRole="employer"
          onSidebarToggle={handleSidebarToggle}
          userName={user?.fullName || 'Employer'}
        />

        <RoleBasedSidebar
          userRole="employer"
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main className={`transition-all duration-300 ease-in-out pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-8">
            <div className="flex items-center justify-center min-h-[500px]">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600 text-lg">Loading application details...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-gray-50">
        <HeaderBar
          userRole="employer"
          onSidebarToggle={handleSidebarToggle}
          userName={user?.fullName || 'Employer'}
        />

        <RoleBasedSidebar
          userRole="employer"
          isCollapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
        />

        <main className={`transition-all duration-300 ease-in-out pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="p-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
              <p className="text-red-600 mb-4">{error || 'Application not found'}</p>
              <button
                onClick={() => navigate('/employer-application-management')}
                className="inline-flex items-center px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                Back to Applications
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || 'Employer'}
      />

      <Notifier
        type={toast.type}
        message={toast.message}
        show={toast.show}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
        duration={3000}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main className={`transition-all duration-300 ease-in-out pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-8 space-y-6">
          <div>
            <BreadcrumbNavigation
              items={[
                { label: 'Dashboard', path: '/employer-dashboard' },
                { label: 'Application Management', path: '/employer-application-management' },
                { label: application.vdrId, path: `/employer-vdr-applications/${vdr_id}` }
              ]}
            />
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{application.vdrId}</h1>
                <p className="text-gray-600">{application.companyName}</p>
                <div className="text-xs text-gray-500 mt-1">Application ID: {application.applicationId}</div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4 lg:mt-0">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <Icon name="Globe" size={14} className="mr-1" />
                  {application.nationality}
                </span>
                {application.vdrType && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <Icon name="Layers" size={14} className="mr-1" />
                    {application.vdrType}
                  </span>
                )}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  application.biodataTypeLabel === 'CLAB'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {application.biodataTypeLabel}
                </span>
                {application.isSelectable && (
                  <button
                    onClick={handleSelectWorkers}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <Icon name="Users" size={16} className="mr-2" />
                    Select Workers
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {summaryItems.map(item => (
                <div key={item.label} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <div className={`text-sm font-medium ${item.accent} mb-1 flex items-center gap-2`}>
                    <Icon name={item.icon} size={16} />
                    {item.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 pt-4">
              <nav className="flex space-x-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`pb-3 text-sm font-medium border-b-2 ${
                    activeTab === 'details'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Application Details
                </button>
                <button
                  onClick={() => setActiveTab('selection')}
                  className={`pb-3 text-sm font-medium border-b-2 ${
                    activeTab === 'selection'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Selected Workers ({selectedWorkers.length})
                </button>
                {rejectedWorkers.length > 0 && (
                  <button
                    onClick={() => setActiveTab('rejected')}
                    className={`pb-3 text-sm font-medium border-b-2 ${
                      activeTab === 'rejected'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Rejected Workers ({rejectedWorkers.length})
                  </button>
                )}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Quota Information</h3>
                    <dl className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <dt className="font-medium text-gray-500">FCL Requested</dt>
                        <dd className="text-gray-900 text-base font-semibold">{application.slots?.requested ?? application.fclRequested}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">Available Slots</dt>
                        <dd className="text-blue-600 text-base font-semibold">{application.isSelectable ? (application.slots?.available ?? 0) : 0}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">Quota Approved</dt>
                        <dd className="text-gray-900">{application.quotaApproved}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">Quota Remaining</dt>
                        <dd className="text-gray-900">{application.quotaRemaining}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
                    <dl className="space-y-2 text-sm text-gray-600">
                      <div>
                        <dt className="font-medium text-gray-500">Quota Expiry</dt>
                        <dd className="text-gray-900">{formatDate(application.validityStartDate)} – {formatDate(application.validityEndDate)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-500">Last Submission</dt>
                        <dd className="text-gray-900">{formatDateTime(application.localStats?.lastSubmissionAt)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}

              {activeTab === 'selection' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nationality</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passport</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selected At</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedWorkers.map(worker => (
                        <tr key={worker.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{worker.worker_id || worker.biodata_id || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{worker.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{worker.nationality}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{worker.passport_no}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <StatusBadge status={worker.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(worker.selected_at || worker.added_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selectedWorkers.length === 0 && (
                    <div className="text-center text-gray-500 py-6">
                      No workers selected yet.
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'rejected' && rejectedWorkers.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nationality</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejection Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rejectedWorkers.map(worker => (
                        <tr key={worker.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{worker.worker_id || worker.biodata_id || '—'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{worker.full_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{worker.nationality}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{worker.rejection_reason || 'No reason provided'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(worker.rejected_at)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => navigate('/employer-biodata-selection', {
                                state: {
                                  vdrApplication: application,
                                  fromVDRFlow: true,
                                  replaceWorkerId: worker.id,
                                  replaceWorkerName: worker.full_name
                                }
                              })}
                              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                            >
                              <Icon name="RotateCcw" size={12} className="mr-1" />
                              Replace
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const mapping = {
    selected: { label: 'Selected', color: 'bg-blue-100 text-blue-700', icon: 'Clock' },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: 'CheckCircle' },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: 'XCircle' }
  };
  const config = mapping[status] || mapping.selected;
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon name={config.icon} size={12} className="mr-1" />
      {config.label}
    </span>
  );
};

export default EmployerVDRApplicationDetails;
